'use client';

import dynamic from 'next/dynamic';
import { memo, Suspense, useEffect, useState } from 'react';
import { isPopupLabOn } from '@/components/dev/PopupLabGate';

// PERFORMANCE CRITICAL: Dynamic imports with no SSR
// Load widgets ONLY after browser is idle to ensure zero impact
const SocialShareWidget = dynamic(
  () => import('./SocialShare/SocialShareWidget').then(mod => ({
    default: mod.OptimizedSocialShareWidget || mod.default
  })),
  { 
    ssr: false,
    loading: () => null
  }
);

const SocialProofPopup = dynamic(
  () => import('./SocialProof/SocialProofPopup').then(mod => ({
    default: mod.OptimizedSocialProofPopup || mod.default
  })),
  { 
    ssr: false,
    loading: () => null
  }
);

// Exit popup v2 (live 2026-10-03, replaced ./ExitIntent/ExitIntentPopup; revert that commit to restore the old one).
// Its own rules decide when and where it shows; see ExitIntentV2/ExitPopupV2.tsx.
const ExitPopupV2 = dynamic(
  () => import('./ExitIntentV2/ExitPopupV2'),
  { ssr: false, loading: () => null }
);

interface GlobalWidgetsProps {
  title?: string;
  description?: string;
}

/**
 * GlobalWidgets - Renders all enabled widgets globally
 * 
 * PERFORMANCE FEATURES:
 * - Dynamic imports prevent bundle bloat
 * - No SSR to avoid hydration issues
 * - Suspense boundaries for error handling
 * - Memoized to prevent unnecessary re-renders
 */
const GlobalWidgets = memo(({ title, description }: GlobalWidgetsProps) => {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    // Popup lab (?popup=new) previews the popups; the live one stays out of that tab.
    if (isPopupLabOn()) return;
    // PERFORMANCE: load after the browser is idle, zero impact on first paint
    if ('requestIdleCallback' in window) {
      const handle = requestIdleCallback(() => setReady(true), { timeout: 2000 });
      return () => cancelIdleCallback(handle);
    }
    const timer = setTimeout(() => setReady(true), 150);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Suspense fallback={null}>
      {/* Social Share Widget */}
      <SocialShareWidget 
        title={title}
        description={description}
      />
      
      {/* Social Proof Popup */}
      <SocialProofPopup />
      
      {/* Exit popup v2 - lazy loaded after idle */}
      {ready && <ExitPopupV2 />}
    </Suspense>
  );
});

GlobalWidgets.displayName = 'GlobalWidgets';

export default GlobalWidgets;